"use client";

import React, { useEffect, useRef, useState } from "react";
import apiService from "@/app/services/apiService";
import toast from "react-hot-toast";
import { useRouter } from "next/navigation";

type CartItem = { id: string; name: string; price: number; quantity: number };

interface Props {
  items: CartItem[];
  setOpenCart?: (open: boolean) => void;
}

type PixCreateResp = {
  payment_id: string | number;
  status: string;
  status_detail?: string;
  external_reference: string;
  qr_code?: string;
  qr_code_base64?: string;
  ticket_url?: string;
  total_amount: string;
};

export default function WalletPixButton({ items, setOpenCart }: Props) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const [pix, setPix] = useState<PixCreateResp | null>(null);
  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollAbort = useRef<ReturnType<typeof setTimeout> | null>(null);

  const buildTicketsPayload = () =>
    items.map((it) => ({ id: it.id, quantity: it.quantity ?? 1, unit_price: it.price }));

  async function startCheckout(e?: React.MouseEvent) {
    e?.stopPropagation();
    if (!items.length) { toast.error("Seu carrinho está vazio."); return; }
    if (loading) return;

    try {
      setLoading(true);
      const tickets = buildTicketsPayload();
      const res = await apiService.postWithToken("/api/shops/tickets/create_pix_payment/", { tickets }) as PixCreateResp;

      if (!res?.qr_code || !res?.qr_code_base64) {
        setLoading(false);
        toast.error("Não recebi o QR do Pix.");
        return;
      }

      setPix(res);
      setOpen(true);
      startPolling(res.external_reference);
    } catch (err) {
      console.error(err);
      toast.error("Falha ao iniciar Pix.");
      setLoading(false);
    }
  }

  async function checkStatus(ref: string) {
    try {
      const s = await apiService.getWithToken(`/api/shops/payments/status/?ref=${encodeURIComponent(ref)}`);
      if (s?.paid === true || s?.status === "approved") {
        stopPolling();
        setOpen(false);
        toast.success("Pagamento aprovado!");
        router.replace(`/payment-pix/success?ref=${encodeURIComponent(ref)}`);
      } else if (["rejected", "cancelled", "canceled", "expired", "refunded", "charged_back"].includes(s?.status)) {
        stopPolling();
        setOpen(false);
        toast.error(s?.status === "expired" ? "Pagamento expirou. Gere um novo Pix." : "Pagamento não aprovado.");
      }
    } catch (e) {
      console.error("status check error", e);
    }
  }

  function startPolling(ref: string) {
    stopPolling();
    pollTimer.current = setInterval(() => checkStatus(ref), 3000);
    pollAbort.current = setTimeout(() => {
      stopPolling();
      setOpen(false);
      toast.error("Tempo esgotado para confirmação do Pix.");
    }, 5 * 60 * 1000);
  }

  function stopPolling() {
    if (pollTimer.current) clearInterval(pollTimer.current);
    if (pollAbort.current) clearTimeout(pollAbort.current);
    pollTimer.current = null;
    pollAbort.current = null;
    setLoading(false); // Garante que o botão destrave ao parar o polling
  }

  useEffect(() => () => stopPolling(), []);

  function copyPix() {
    if (!pix?.qr_code) return;
    navigator.clipboard.writeText(pix.qr_code).then(
      () => toast.success("Código Pix copiado!"),
      () => toast.error("Não foi possível copiar.")
    );
  }

  // Função utilitária para tratar o formato da string Base64 dinamicamente
  const formatBase64Src = (src: string) => {
    if (src.startsWith("data:image")) return src;
    return `data:image/png;base64,${src}`;
  };

  return (
    <>
      <button
        onClick={startCheckout}
        disabled={loading || !items.length}
        className="w-full bg-green-600 text-white font-semibold py-2 px-4 rounded hover:bg-green-700 transition-colors disabled:opacity-60"
      >
        {loading ? "Gerando Pix…" : "Pagar com Pix"}
      </button>

      {open && pix && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[1000] flex items-center justify-center" role="dialog" aria-modal="true">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
            <div className="flex items-start justify-between">
              <h3 className="text-lg font-semibold">Pague com Pix</h3>
              <button
                 onClick={() => { stopPolling(); setOpen(false); setPix(null); }}
                 className="text-gray-400 hover:text-gray-600 text-xl"
                 aria-label="Fechar"
              >
                 ✖
              </button>
            </div>

            <p className="text-gray-600 mt-2">Escaneie o QR ou use “copia e cola”.</p>

            <div className="mt-4 flex flex-col items-center">
              {pix.qr_code_base64 ? (
                <img src={formatBase64Src(pix.qr_code_base64)} alt="QR Pix" className="w-64 h-64" />
              ) : (
                <div className="w-64 h-64 bg-gray-100 rounded flex items-center justify-center text-gray-400">QR indisponível</div>
              )}
              <div className="mt-2 text-xs text-gray-500">
                Ref: <span className="font-mono">{pix.external_reference}</span>
              </div>
            </div>

            <div className="mt-4">
              <div className="bg-gray-100 rounded p-3 text-xs break-all font-mono max-h-32 overflow-auto">
                {pix.qr_code}
              </div>
              <div className="mt-2 flex gap-2">
                <button onClick={copyPix} className="px-3 py-2 rounded bg-neutral-900 text-white text-sm hover:bg-neutral-800 transition-colors">
                  Copiar código
                </button>
                {pix.ticket_url && (
                  <a href={pix.ticket_url} target="_blank" rel="noreferrer" className="px-3 py-2 rounded border text-sm hover:bg-gray-50 transition-colors">
                    Abrir no Mercado Pago
                  </a>
                )}
              </div>
            </div>

            <div className="mt-4 text-sm text-gray-500 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
              A confirmação aparecerá automaticamente.
            </div>
          </div>
        </div>
      )}
    </>
  );
}
